import React from "react";
import "./PrivacyPolicy.css";

function PrivacyPolicy() {
  return (
    <div className="privacy-container">
      <a href="/" className="back-link">← Back to Colordle</a>
      
      <h1>Privacy Policy</h1>
      <p className="last-updated">Last Updated: February 27, 2026</p>

      <hr />

      <h2>1. Introduction</h2>
      <p>
        This Privacy Policy describes how Colordle ("we," "us," or "our") collects, 
        uses, and shares information when you use our website at colordle.ryantanen.com 
        (the "Service"). By using the Service, you agree to the collection and use of 
        information in accordance with this policy.
      </p>
      <p>
        Colordle is a free-to-play color guessing game inspired by Wordle and similar 
        daily puzzle games. We are committed to protecting your privacy and being 
        transparent about our data practices. This policy explains what information 
        we collect, how we use it, and what choices you have.
      </p>
      <p>
        Please read this Privacy Policy carefully. If you do not agree with the terms 
        of this Privacy Policy, please do not access or use the Service.
      </p>

      <h2>2. Information We Collect</h2>
      <p>
        We collect minimal information necessary to provide and improve the Service. 
        The information we collect falls into two categories: data stored locally on 
        your device and anonymous analytics data.
      </p>
      
      <h3>2.1 Local Storage Data</h3>
      <p>
        We store game-related data locally in your browser using localStorage. This 
        is a standard web technology that allows websites to store data on your device. 
        The data stored includes:
      </p>
      <ul>
        <li>Your current game state, including your guesses for today's puzzle</li>
        <li>Your win streak count</li>
        <li>A record of unique colors you have guessed over time</li>
        <li>Whether you have completed today's puzzle</li>
      </ul>
      <p>
        This data is stored entirely on your device and is not transmitted to our 
        servers or any third party. You have full control over this data and can 
        delete it at any time by clearing your browser's localStorage or site data.
      </p>
      <p>
        If you clear your browser data or use a different browser or device, your 
        game progress and streak will not carry over, as we do not store this 
        information on any server.
      </p>

      <h3>2.2 Analytics Data</h3>
      <p>
        We use Firebase Analytics and Google Analytics to collect anonymous usage 
        data that helps us understand how users interact with the Service and 
        identify areas for improvement. This data is collected automatically when 
        you visit the Service and may include:
      </p>
      <ul>
        <li>Pages visited and time spent on each page</li>
        <li>General geographic location at the country or region level (not precise location)</li>
        <li>Device type (mobile, tablet, desktop)</li>
        <li>Operating system and version</li>
        <li>Browser type and version</li>
        <li>Screen resolution</li>
        <li>Referring website (how you arrived at the Service)</li>
        <li>Game events such as completing a puzzle or achieving a win</li>
      </ul>
      <p>
        This analytics data is aggregated and anonymized. It does not include any 
        personally identifiable information such as your name, email address, or 
        precise location. We cannot identify individual users from this data.
      </p>

      <h3>2.3 Information We Do Not Collect</h3>
      <p>
        We want to be clear about what we do not collect:
      </p>
      <ul>
        <li>We do not collect your name, email address, or contact information</li>
        <li>We do not require account registration or login</li>
        <li>We do not collect payment information</li>
        <li>We do not access your camera, microphone, or other device sensors</li>
        <li>We do not collect precise geolocation data</li>
        <li>We do not collect or store IP addresses for our own use</li>
      </ul>

      <h2>3. How We Use Information</h2>
      <p>
        We use the information we collect for the following purposes:
      </p>
      <ul>
        <li>
          <strong>Game Functionality:</strong> Local storage data is used to preserve 
          your game progress between sessions, maintain your win streak, and provide 
          a seamless gaming experience.
        </li>
        <li>
          <strong>Service Improvement:</strong> Analytics data helps us understand 
          which features are popular, identify technical issues, and prioritize 
          improvements to the Service.
        </li>
        <li>
          <strong>Performance Monitoring:</strong> We use Firebase Performance 
          Monitoring to track page load times and identify performance bottlenecks 
          that may affect user experience.
        </li>
        <li>
          <strong>Usage Analysis:</strong> Aggregate analytics help us understand 
          general usage patterns, such as peak usage times and popular devices or 
          browsers.
        </li>
      </ul>
      <p>
        We do not sell your personal information to third parties. Advertising on 
        the Service is managed by Playwire LLC, which may collect and use certain 
        aggregated and anonymized data for advertising purposes as described in 
        Section 5.
      </p>

      <h2>4. Cookies and Tracking Technologies</h2>
      <p>
        The Service uses cookies and similar tracking technologies. Cookies are 
        small text files that are placed on your device when you visit a website. 
        They are widely used to make websites work more efficiently and to provide 
        information to website owners.
      </p>
      <p>
        The cookies used on the Service are primarily set by our third-party 
        analytics providers (Firebase and Google Analytics). These cookies help 
        us understand how users interact with the Service by collecting information 
        such as pages visited, time on site, and navigation patterns.
      </p>
      <p>
        You can control cookies through your browser settings. Most browsers allow 
        you to refuse cookies, delete existing cookies, or be notified when a cookie 
        is set. However, please note that disabling cookies may affect the 
        functionality of some features of the Service.
      </p>
      <p>
        For more information about the cookies used by Google Analytics, please 
        visit Google's privacy policy and cookie information pages.
      </p>

      <h2>5. Third-Party Services</h2>
      <p>
        We use the following third-party services to operate and improve the Service:
      </p>
      <ul>
        <li>
          <strong>Firebase (Google):</strong> We use Firebase for analytics, 
          performance monitoring, and hosting. Firebase is a platform provided by 
          Google that helps developers build and improve applications.
        </li>
        <li>
          <strong>Google Analytics:</strong> We use Google Analytics to collect 
          and analyze anonymous usage data about how visitors use the Service.
        </li>
        <li>
          <strong>Playwire:</strong> All or partial advertising on this Website 
          or App is managed by Playwire LLC. If Playwire publisher advertising 
          services are used, Playwire LLC may collect and use certain aggregated 
          and anonymized data for advertising purposes. To learn more about the 
          types of data collected, how data is used and your choices as a user, 
          please visit <a href="https://www.playwire.com/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.playwire.com/privacy-policy</a>.
        </li>
      </ul>
      <p>
        These third-party services have their own privacy policies governing the 
        collection and use of information. We encourage you to review their privacy 
        policies:
      </p>
      <ul>
        <li>Google Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
        <li>Firebase Privacy: <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">https://firebase.google.com/support/privacy</a></li>
        <li>Playwire Privacy Policy: <a href="https://www.playwire.com/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.playwire.com/privacy-policy</a></li>
      </ul>
      <p>
        We do not control and are not responsible for the privacy practices of these 
        third-party services. Your use of these services is subject to their 
        respective terms and privacy policies.
      </p>

      <h2>6. Data Security</h2>
      <p>
        We take reasonable measures to protect the information associated with the 
        Service. The Service is hosted on Firebase Hosting, which provides secure 
        HTTPS connections for all traffic.
      </p>
      <p>
        However, please be aware that no method of transmission over the Internet 
        or method of electronic storage is 100% secure. While we strive to use 
        commercially acceptable means to protect your information, we cannot 
        guarantee its absolute security.
      </p>
      <p>
        Since game data is stored locally on your device, the security of that data 
        depends on the security of your device and browser. We recommend keeping 
        your browser and operating system up to date with the latest security patches.
      </p>

      <h2>7. Data Retention</h2>
      <p>
        Game data stored in your browser's localStorage remains on your device until 
        you clear your browser data, clear site data for the Service specifically, 
        or manually remove it. We do not have access to this data and cannot delete 
        it on your behalf.
      </p>
      <p>
        Analytics data collected by Firebase and Google Analytics is retained 
        according to their default retention policies. Google Analytics data is 
        typically retained for 26 months by default. You can learn more about 
        Google's data retention practices in their privacy documentation.
      </p>

      <h2>8. Your Rights and Choices</h2>
      <p>
        You have several rights and choices regarding your information:
      </p>
      <ul>
        <li>
          <strong>Clear Local Data:</strong> You can clear your localStorage data 
          at any time through your browser settings. This will reset your game 
          progress and streak.
        </li>
        <li>
          <strong>Opt Out of Analytics:</strong> You can opt out of Google Analytics 
          tracking by installing the Google Analytics Opt-out Browser Add-on, 
          available at <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">https://tools.google.com/dlpage/gaoptout</a>.
        </li>
        <li>
          <strong>Browser Settings:</strong> You can configure your browser to 
          reject cookies or notify you when cookies are being set.
        </li>
        <li>
          <strong>Do Not Track:</strong> Some browsers offer a "Do Not Track" 
          setting. However, there is no industry standard for how websites should 
          respond to these signals, and we do not currently respond to Do Not Track 
          signals.
        </li>
        <li>
          <strong>Contact Us:</strong> You can contact us with questions about our 
          data practices using the contact information provided below.
        </li>
      </ul>

      <h2>9. Children's Privacy</h2>
      <p>
        The Service is intended for a general audience and is not specifically 
        directed at children under the age of 13. We do not collect personal 
        information directly from any users, including children.
      </p>
      <p>
        The only data collected is anonymous analytics information through 
        third-party services (Firebase and Google Analytics), which does not 
        personally identify any individual user regardless of age. Game progress 
        is stored locally in the user's browser and is never transmitted to our 
        servers.
      </p>
      <p>
        Parents and guardians who have questions about our data practices or 
        concerns about their children's use of the Service may contact us at 
        the email address provided in Section 11 below.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time to reflect changes in 
        our practices, technology, legal requirements, or other factors. When we 
        make changes, we will update the "Last Updated" date at the top of this 
        page.
      </p>
      <p>
        We encourage you to review this Privacy Policy periodically to stay 
        informed about our data practices. Your continued use of the Service 
        after any changes to this Privacy Policy constitutes your acceptance of 
        the revised policy.
      </p>
      <p>
        If we make material changes to how we treat your information, we will 
        make reasonable efforts to notify users, such as by posting a notice on 
        the Service.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        If you have any questions, concerns, or requests regarding this Privacy 
        Policy or our data practices, please contact us at:
      </p>
      <p>
        <strong>Email:</strong> <a href="mailto:rjtanenholz@gmail.com">rjtanenholz@gmail.com</a>
      </p>
      <p>
        We will make reasonable efforts to respond to your inquiry in a timely 
        manner.
      </p>

      <hr />

      <p className="footer-text">
        <a href="/">← Return to Colordle</a>
      </p>
    </div>
  );
}

export default PrivacyPolicy;
